import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GetCookie } from '@reynolds/cookiemanager';
import { CreateAcctInp, Customer, VehicleRecs } from 'src/models';
import { IStoreState } from 'src/store';
import { ViewMode } from 'src/enums';
import * as acBase from 'src/store/states/GenericActionCreator';
import { gmDealerEligibility } from '../Specs/SpecsSlice';

export interface ICustomerState {
    Customer: Customer;
    errMsg?: string;
    textCheckReturned?: boolean;
    retCode?: string;
    changeCustReturned: boolean;
    sameFirstNames?: boolean;
    sameLastNames?: boolean;
    sameEmail?: boolean;
    multipleCustomers?: boolean;
    identityVerified: boolean;
    verifyingIdentity: boolean;
    sentSecurityCode: boolean;
    loggingCustOff: boolean;
    mfaContact: string[];
    loading: 'idle' | 'pending' | 'succeeded' | 'failed';
    gmMemberErr: string;
    gmAcctErr: string;
    gmRewardsLoading: boolean;
    vinCustReturned: boolean;
}

/** Initial State */
export const initialState: ICustomerState = {
    Customer: {
        nameId: '',
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        login: false,
        prefContact: '',
        textOptIn: '',
        emailOptIn: '',
        streetAddress: '',
        city: '',
        stateAddress: '',
        zipCode: '',
        vehicleList: [],
        phoneType: '',
        addVehNameId: '',
        hasAccount: false,
        isCompany: false,
        gmRedeemAmt: '',
        encryptFirst: '',
        encryptLast: '',
        encryptEmail: '',
        encryptPhone: '',
        ascCustId: '',
    },
    errMsg: '',
    retCode: '',
    textCheckReturned: false,
    changeCustReturned: false,
    sameFirstNames: false,
    sameLastNames: false,
    sameEmail: false,
    multipleCustomers: false,
    identityVerified: false,
    verifyingIdentity: false,
    sentSecurityCode: false,
    loggingCustOff: false,
    mfaContact: [],
    loading: 'idle',
    gmMemberErr: '',
    gmAcctErr: '',
    gmRewardsLoading: false,
    vinCustReturned: false,
};

const phoneRegex = new RegExp(/^(\d{3})(\d{3})(\d{4})$/);

interface GetOEMVehiclesParams {
    inp: Customer;
}

export const getOEMVehicles = createAsyncThunk<
    { errMsg: string; vehicleList: VehicleRecs },
    GetOEMVehiclesParams,
    { state: IStoreState }
>(
    'customer/getOEMVehicles',
    async ({ inp }, thunkApi) => {
        const state = thunkApi.getState();
        const logId = state.Framework.logId;
        const store = state.Framework.store;
        const branch = state.Framework.branch;
        const profileNum = state.Framework.specProfile;
        const initialVehicles = state.Customer.Customer.vehicleList;
        const callingProgram = 'SVP';

        const reqData = {
            email: inp.emailAddress.replace(/'/g, '&quot'),
            phone: inp.phoneNumber,
            firstName: inp.firstName.replace(/'/g, ''),
            lastName: inp.lastName.replace(/'/g, ''),
            companyName: '',
            zipCode: '',
            address: '',
            city: '',
            state: '',
            profile: profileNum,
            logId: logId,
            nameId: inp.nameId,
            callingProgram: callingProgram,
            numVehicles: initialVehicles?.length ?? 0,
        };
        const response = await acBase.eraWebServiceCall<Customer>('SVPController', 'OEMCustSearch', reqData, store, branch);

        if (response.errMsg) {
            return {
                errMsg: response.errMsg,
                vehicleList: initialVehicles ?? [],
            };
        }

        const list = initialVehicles ? [...initialVehicles, ...response.vehicleList] : response.vehicleList;

        return {
            errMsg: '',
            vehicleList: list,
        };
    },
);

interface GetCustomersParams {
    inp: Customer;
    profile: string;
    store: string;
    branch: string;
    searchForSPM: string;
    landingPageFlag: string;
}

export const getCustomers = createAsyncThunk<
    {
        mfaContact: string[];
        errMsg: string;
        retCode: string;
        customer: ICustomerState;
    },
    GetCustomersParams,
    { state: IStoreState }
>(
    'customer/getCustomers',
    async ({ inp, profile: profileNum, store, branch, searchForSPM, landingPageFlag }, thunkApi) => {
        const state = thunkApi.getState();
        const logId = state.Framework.logId;
        let callingProgram = 'SVP';
        const viewMode = state.Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }

        const oemSearchLaunched = state.Framework.toyotaCustSearchLaunched === 'Y' ||
            state.Framework.lexusCustSearchLaunched === 'Y' || state.Framework.lexusTDCSLaunched === 'Y' ||
            state.Framework.toyotaTDCSLaunched === 'Y';

        const reqData = {
            email: inp.emailAddress.replace(/'/g, '&quot'),
            phone: inp.phoneNumber,
            firstName: inp.firstName.replace(/'/g, ''),
            lastName: inp.lastName.replace(/'/g, ''),
            profile: profileNum,
            logId: logId,
            nameId: inp.nameId,
            allowMulti: 'Y',
            callingProgram: callingProgram,
            oemSearchLaunched: oemSearchLaunched,
            searchForSPM: searchForSPM,
            landingPageFlag: landingPageFlag,
        };
        reqData.firstName = reqData.firstName.replace(/'/g, '/quot');

        const response = await acBase.eraWebServiceCall<Customer>('SVPController', 'CustSearch', reqData, store, branch);

        response.login = inp.login ? true : false;

        const { customersFound, ...res} = response;
        const { message, errMsg, retCode } = response;

        const sameFirstNames = res.sameFirstNames === 'Y' ? true : false;
        const sameLastNames = res.sameLastNames === 'Y' ? true : false;
        const sameEmail = res.sameEmail === 'Y' ? true : false;
        const multipleCustomers = parseInt(customersFound) === 1 ? false : true;

        let mfaContact = [] as string[];
        let errorMsg = '';

        if (!multipleCustomers || !customersFound) {
            mfaContact = [response.textPhone1, response.textPhone2, response.mfaEmail];
        }

        if (message) {
            errorMsg = '999';
        } else if (errMsg) {
            errorMsg = errMsg;
        }

        if (errorMsg !== '') {
            return {
                errMsg: errorMsg,
                retCode: retCode ?? '',
                mfaContact: [],
                customer: initialState,
            };
        }
        const { stateAddress, city, zipCode, streetAddress, ascCustId } = response;

        const customer = {
            ...res,
            stateAddress: stateAddress ? stateAddress : initialState.Customer.stateAddress,
            city: city ? city : initialState.Customer.city,
            zipCode: zipCode ? zipCode : initialState.Customer.zipCode,
            streetAddress: streetAddress ? streetAddress : initialState.Customer.streetAddress,
            ascCustId: ascCustId ? ascCustId : initialState.Customer.ascCustId,
        };

        const newState: ICustomerState = {
            Customer: customer,
            errMsg: errMsg ? errMsg: '',
            retCode: retCode,
            changeCustReturned: false,
            sameFirstNames: sameFirstNames,
            sameLastNames: sameLastNames,
            sameEmail: sameEmail,
            multipleCustomers: multipleCustomers,
            identityVerified: false,
            verifyingIdentity: false,
            sentSecurityCode: false,
            loggingCustOff: false,
            mfaContact: [],
            loading: 'idle',
            gmMemberErr: '',
            gmAcctErr: '',
            gmRewardsLoading: false,
            vinCustReturned: false,
        };

        return {
            mfaContact: mfaContact,
            errMsg: '',
            retCode: retCode ?? '',
            customer: newState,
        };
    },
);

export const CustomerSlice = createSlice({
    name: 'Customer',
    initialState: initialState,
    reducers: {
        createAcct: (state, action: PayloadAction<Customer>) => {
            state.Customer = action.payload;
        },
        updateCustomer: (state, action: PayloadAction<Customer>) => {
            state.Customer = action.payload;
        },
        loginCustomer: (state, action: PayloadAction<Customer>) => {
            state.Customer = action.payload;
            state.sameFirstNames = true;
        },
        resetPassword: (state, action: PayloadAction<Customer>) => {
            state.Customer = action.payload;
        },
        clearError: (state) => {
            state.errMsg = '';
            state.retCode = '';
        },
        customerError: (state, action: PayloadAction<string | undefined>) => {
            state.errMsg = action.payload;
        },
        changeCustInfo: (state, action: PayloadAction<ICustomerState>) => {
            if (action.payload.Customer) {
                state.Customer = action.payload.Customer;
            }
            state.errMsg = action.payload.errMsg;
            state.retCode = action.payload.retCode;
            state.changeCustReturned = true;
        },
        setChangeCustReturned: (state, action: PayloadAction<boolean>) => {
            state.changeCustReturned = action.payload;
        },
        clearCustInfo: (state, action: PayloadAction<ICustomerState>) => {
            state.Customer = action.payload.Customer;
            state.errMsg = action.payload.errMsg;
            state.textCheckReturned = action.payload.textCheckReturned;
            state.identityVerified = false;
            state.verifyingIdentity = false;
        },
        checkPhoneOptIn: (state, action: PayloadAction<string>) => {
            state.Customer.textOptIn = action.payload;
            state.textCheckReturned = true;
        },
        clearTextCheckReturned: (state) => {
            state.textCheckReturned = false;
        },
        sendResetEmail: (state, action: PayloadAction<string>) => {
            state.errMsg = action.payload;
        },
        checkUniqueEmail: (state, action: PayloadAction<string>) => {
            state.errMsg = action.payload;
        },
        getExCust: (state, action: PayloadAction<Customer>) => {
            state.Customer = action.payload;
        },
        sendSecurityCode: (state, action: PayloadAction<boolean>) => {
            state.sentSecurityCode = action.payload;
        },
        verifyIdentity: (state,
            action: PayloadAction<
            {
                identityVerified: boolean,
                firstName: string,
                lastName: string,
                emailAddress: string,
                phoneNumber: string,
                errMsg: string,
                login: boolean,
            }
        >) => {
            state.identityVerified = action.payload.identityVerified;
            state.errMsg = action.payload.errMsg;
            state.Customer.firstName = action.payload.firstName;
            state.Customer.lastName = action.payload.lastName;
            state.Customer.emailAddress = action.payload.emailAddress;
            state.Customer.phoneNumber = action.payload.phoneNumber;
            state.Customer.login = action.payload.login;
        },
        setVerifyingIdentity: (state, action: PayloadAction<boolean>) => {
            state.verifyingIdentity = action.payload;
        },
        checkAuthToken: (state, action: PayloadAction<string>) => {
            state.identityVerified = action.payload === '0000' ? true : false;
        },
        checkAuthTokenOSD: (state, action: PayloadAction<string>) => {
            state.identityVerified = action.payload === '0000' ? true : false;
        },
        setIdentityVerified: (state, action: PayloadAction<boolean>) => {
            state.identityVerified = action.payload;
        },
        setMFAContact: (state, action: PayloadAction<string[]>) => {
            state.mfaContact = action.payload;
        },
        waitForLogOff: (state) => {
            state.loggingCustOff = true;
        },
        logCustOff: (state) => {
            state.loggingCustOff = false;
        },
        setLoading: (state, action: PayloadAction<'idle' | 'pending' | 'succeeded' | 'failed'>) => {
            state.loading = action.payload;
        },
        gmMemberSearch: (state, action: PayloadAction<{
            gmRedeemAmt: string,
            gmMemberErr: string,
        }>) => {
            state.gmMemberErr = action.payload.gmMemberErr;
            state.Customer.gmRedeemAmt = action.payload.gmRedeemAmt;
        },
        clearGMInfo: (state) => {
            state.gmMemberErr = '';
            state.Customer.gmRedeemAmt = '';
            state.gmAcctErr = '';
        },
        setGMRewardsLoading: (state, action: PayloadAction<boolean>) => {
            state.gmRewardsLoading = action.payload;
        },
        vinCustSearch: (state, action: PayloadAction<Customer>) => {
            state.Customer = action.payload;
        },
        setVinCustReturned: (state, action: PayloadAction<boolean>) => {
            state.vinCustReturned = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getOEMVehicles.fulfilled, (state, action: PayloadAction<{errMsg: string, vehicleList: VehicleRecs}>) => {
            state.Customer.vehicleList = action.payload.vehicleList;
            state.errMsg = action.payload.errMsg;
            state.loading = 'succeeded';
        });
        builder.addCase(getOEMVehicles.pending, (state) => {
            state.loading = 'pending';
        });
        builder.addCase(getCustomers.fulfilled, (state, action: PayloadAction<{mfaContact: string[], errMsg: string,
        customer: ICustomerState, retCode: string}>) => {
            state.Customer = action.payload.customer.Customer;
            state.multipleCustomers = action.payload.customer.multipleCustomers;
            state.sameFirstNames = action.payload.customer.sameFirstNames ?? false;
            state.sameLastNames = action.payload.customer.sameLastNames ?? false;
            state.sameEmail = action.payload.customer.sameEmail ?? false;
            state.mfaContact = action.payload.mfaContact;
            state.errMsg = action.payload.errMsg;
            state.retCode = action.payload.retCode;
            state.loading = 'succeeded';
        });
        builder.addCase(getCustomers.pending, (state) => {
            state.loading = 'pending';
        });
    },
});

export const {
    createAcct,
    updateCustomer,
    loginCustomer,
    resetPassword,
    clearError,
    customerError,
    changeCustInfo,
    setChangeCustReturned,
    clearCustInfo,
    checkPhoneOptIn,
    clearTextCheckReturned,
    sendResetEmail,
    checkUniqueEmail,
    getExCust,
    sendSecurityCode,
    verifyIdentity,
    setVerifyingIdentity,
    checkAuthToken,
    checkAuthTokenOSD,
    setIdentityVerified,
    setMFAContact,
    waitForLogOff,
    logCustOff,
    setLoading,
    gmMemberSearch,
    clearGMInfo,
    setGMRewardsLoading,
    vinCustSearch,
    setVinCustReturned,
} = CustomerSlice.actions;

export default CustomerSlice.reducer;

export function CreateAccount(inp: CreateAcctInp, store: string, branch: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const logId = getState().Specs.specs?.logId;
        const previewFlag = getState().Framework.previewFlag;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            firstName: inp.firstName.replace(/'/g, ''),
            lastName: inp.lastName.replace(/'/g, ''),
            phoneNumber: inp.phoneNumber,
            email: inp.emailAddress.replace(/'/g, '&quot'),
            pwd: inp.pwd.replace(/'/g, '&quot'),
            logId: logId,
            previewFlag: previewFlag,
            callingProgram: callingProgram,
        };

        const response = await acBase.eraWebServiceCall<Customer>('SVPController', 'CreateAccount', reqData, store, branch);
        const newState: Customer = {
            ...response,
        };
        if ((response as Customer)?.errMsg && response.retCode !== '8000') {
            dispatch(customerError((response as Customer).errMsg));
        } else if (response.retCode) {
            if (response.retCode === '8000') {
                dispatch(customerError(response.retCode));
            }
        } else {
            dispatch(createAcct(newState));
        }
    };
}

export function OEMCustomerSearch(inp: Customer) {
    return (dispatch) => {
        const reqData = {
            inp,
        };
        dispatch(getOEMVehicles(reqData));
    };
}

export function SetLoadingStateToIdle() {
    return (dispatch) => {
        dispatch(setLoading('idle'));
    };
}

export function CustomerSearch(
    inp: Customer,
    profileNum: string,
    store: string,
    branch: string,
    searchForSPM?: string,
    landingPageFlag?: string) {
    return (dispatch) => {
        const reqData = {
            profile: profileNum,
            store: store,
            branch: branch,
            inp: inp,
            searchForSPM: searchForSPM ?? '',
            landingPageFlag: landingPageFlag ?? '',
        };

        dispatch(getCustomers(reqData));
    };
}

export function LoginCustomer(inp: CreateAcctInp, profileNum: string, store: string, branch: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const logId = getState().Specs.specs?.logId;
        // const identityVerified = inp.identityVerified ? inp.identityVerified : getState().Customer.identityVerified;
        const authToken = GetCookie('authToken');
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            email: inp.emailAddress.replace(/'/g, '&quot'),
            pwd: inp.pwd.replace(/'/g, '&quot'),
            profile: profileNum,
            logId: logId,
            callingProgram: callingProgram,
            authToken: authToken,
        };
        const response = await acBase.eraWebServiceCall<Customer>('SVPController', 'LG', reqData, store, branch);
        const errMsg = response?.errMsg;
        if (errMsg && response.retCode !== '8000') {
            dispatch(customerError(errMsg));
        } else if (response.retCode === '8000') {
            dispatch(customerError(response.retCode));
        } else {
            const { stateAddress, city, zipCode, streetAddress, login, ascCustId } = response;
            const customer = {
                ...response,
                stateAddress: stateAddress ? stateAddress : initialState.Customer.stateAddress,
                city: city ? city : initialState.Customer.city,
                zipCode: zipCode ? zipCode : initialState.Customer.zipCode,
                streetAddress: streetAddress ? streetAddress : initialState.Customer.streetAddress,
                login: login === '1' ? true : false,
                ascCustId: ascCustId ? ascCustId : initialState.Customer.ascCustId,
            };
            dispatch(loginCustomer(customer));
            // Identity verification is always required; mirror the login status directly.
            dispatch(setIdentityVerified(customer.login));

            if (!customer.login) {
                dispatch(setVerifyingIdentity(true));
                dispatch(setMFAContact([response.textPhone1, response.textPhone2, response.emailAddress]));
            } else {
                dispatch(setVerifyingIdentity(false));
            }
        }
    };
}

export function CheckPhoneOptIn(custId: string, profileNum: string, store: string, branch: string, phone?: string) {
    return async (dispatch, getState) => {
        const logId = getState().Framework.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            custId: custId,
            profile: profileNum,
            phone: phone ?? '',
            logId: logId,
            callingProgram: callingProgram,
        };
        const response = await acBase.eraWebServiceCall<Customer>('SVPController', 'CheckPhoneOptIn', reqData, store, branch);
        if (response) {
            const { textOptIn, retCode } = response;
            if (retCode && retCode === '8000') {
                dispatch(customerError(retCode));
            } else {
                dispatch(checkPhoneOptIn(textOptIn));
            }
        }
    };
}

export function UpdateCustInfo(customer: Customer) {
    return (dispatch) => {
        if (!customer.nameId) {
            dispatch(verifyIdentity({
                identityVerified: false,
                firstName: '',
                lastName: '',
                emailAddress: '',
                phoneNumber: '',
                errMsg: '',
                login: false,
            }));
        }
        dispatch(updateCustomer(customer));
    };
}

export function CustomerClearError() {
    return (dispatch) => {
        dispatch(clearError());
    };
}

export function ClearTextCheckReturned() {
    return (dispatch) => {
        dispatch(clearTextCheckReturned());
    };
}

export function SendResetEmail(email: string, namedId: string, profileNum: string, store: string, branch: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const logId = getState().Specs.specs?.logId;
        const previewFlag = getState().Framework.previewFlag;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            email: email,
            nameId: namedId,
            profile: profileNum,
            logId: logId,
            previewFlag: previewFlag,
            callingProgram: callingProgram,
        };
        const response = await acBase.eraWebServiceCall<Customer>('SVPController', 'SendResetEmail', reqData, store, branch);
        const { errMsg } = response;
        if (errMsg && response.retCode !== '8000') {
            dispatch(sendResetEmail(errMsg));
        } else if (response.retCode) {
            if (response.retCode === '8000') {
                dispatch(customerError(response.retCode));
            }
        } else {
            dispatch(sendResetEmail(''));
        }
    };
}

export function ResetCustPassword(resetObject: { inputAcctId: string, custEmail: string, newPassword: string },
    profileNum: string, store: string, branch: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const logId = getState().Specs.specs?.logId;
        const previewFlag = getState().Framework.previewFlag;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            custId: resetObject.inputAcctId,
            email: resetObject.custEmail,
            pwd: resetObject.newPassword,
            profile: profileNum,
            logId: logId,
            previewFlag: previewFlag,
            callingProgram: callingProgram,
        };
        const response = await acBase.eraWebServiceCall<Customer>('SVPController', 'ResetPassword', reqData, store, branch);
        const {errMsg } = response;
        if (errMsg && response.retCode !== '8000') {
            dispatch(customerError(errMsg));
        } else if (response.retCode) {
            dispatch(customerError(response.retCode));
        } else {
            const { stateAddress, city, zipCode, streetAddress } = response;
            const customer = {
                ...response,
                emailAddress: !resetObject.custEmail || resetObject.custEmail.includes('*') ?
                    response.emailAddress:resetObject.custEmail,
                stateAddress: stateAddress ? stateAddress : initialState.Customer.stateAddress,
                city: city ? city : initialState.Customer.city,
                zipCode: zipCode ? zipCode : initialState.Customer.zipCode,
                streetAddress: streetAddress ? streetAddress : initialState.Customer.streetAddress,

            };
            dispatch(resetPassword(customer));
        }
    };
}

export function ChangeCustInfo(customer: Customer, profileNum: string, store: string, branch: string, previewFlag) {
    return async (dispatch, getState: () => IStoreState) => {
        // let inpHideData = hideData;
        const identityVerified = getState().Customer.identityVerified;
        const sentSecurityCode = getState().Customer.sentSecurityCode;
        const verifyingIdentity = getState().Customer.verifyingIdentity;
        const login = getState().Customer.Customer.login ?? false;
        const hasAccount = getState().Customer.Customer.hasAccount;
        const gmRedeemAmt = getState().Customer.Customer.gmRedeemAmt;
        const ascCustId = customer.ascCustId;

        // if (login) {
        //     inpHideData = false;
        // }

        const logId = getState().Specs.specs?.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            ...customer,
            profile: profileNum,
            previewFlag: previewFlag,
            logId: logId,
            // hideData: inpHideData,
            callingProgram: callingProgram,
        };
        const response = await acBase.eraWebServiceCall<ICustomerState>('SVPController', 'ChangeCustInfo', reqData, store, branch);
        const { textOptIn, errMsg, retCode } = response;
        if (retCode && retCode === '8000') {
            dispatch(customerError(retCode));
        } else {
            let phoneNumber = !customer.phoneNumber || customer.phoneNumber.includes('*') ?
                response.phoneNumber:customer.phoneNumber;
            // make sure the phone number is formated on return
            const phoneMatch = phoneNumber?.match(phoneRegex) ? phoneNumber?.match(phoneRegex) : false;
            if (phoneMatch) {
                phoneNumber = `(${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}`;
            }
            let streetAddress = response.streetAddress ? response.streetAddress : initialState.Customer.streetAddress;
            let city = response.city ? response.city : initialState.Customer.city;
            let stateAddress = response.stateAddress ? response.stateAddress : initialState.Customer.stateAddress;
            let zipCode = response.zipCode ? response.zipCode : initialState.Customer.zipCode;
            if (customer.streetAddress) {
                streetAddress = customer.streetAddress;
            }
            if (customer.city) {
                city = customer.city;
            }
            if (customer.stateAddress) {
                stateAddress = customer.stateAddress;
            }
            if (customer.zipCode) {
                zipCode = customer.zipCode;
            }
            let newStateCust: Customer = {
                ...response,
                firstName: !customer.firstName || customer.firstName.includes('*') ? response.firstName:customer.firstName,
                lastName: !customer.lastName || customer.lastName.includes('*') ? response.lastName:customer.lastName,
                phoneNumber: phoneNumber,
                emailAddress: !customer.emailAddress || customer.emailAddress.includes('*') ?
                    (response.emailAddress ? response.emailAddress : customer.emailAddress) : customer.emailAddress,
                textOptIn: textOptIn ? textOptIn : '',
                // streetAddress: response.streetAddress ? response.streetAddress : initialState.Customer.streetAddress,
                // city: response.city ? response.city : initialState.Customer.city,
                // stateAddress: response.stateAddress ? response.stateAddress : initialState.Customer.stateAddress,
                // zipCode: response.zipCode ? response.zipCode : initialState.Customer.zipCode,
                streetAddress: streetAddress,
                city: city,
                stateAddress: stateAddress,
                zipCode: zipCode,
                gmRedeemAmt: gmRedeemAmt,
                ascCustId: response.ascCustId ? response.ascCustId : ascCustId,
            };
            if (errMsg || retCode) {
                newStateCust = getState().Customer.Customer;
            }
            newStateCust = {
                ...newStateCust,
                login: login,
                hasAccount: hasAccount,
            };
            /* if (inpHideData) {
                newStateCust.firstName = customer.firstName;
                newStateCust.lastName = customer.lastName;
                newStateCust.phoneNumber = customer.phoneNumber;
                newStateCust.emailAddress = customer.emailAddress;
            } */
            const newState: ICustomerState = {
                Customer: newStateCust,
                errMsg: errMsg ? errMsg : '',
                retCode: retCode ? retCode : '',
                changeCustReturned: true,
                identityVerified: identityVerified,
                verifyingIdentity: verifyingIdentity,
                sentSecurityCode: sentSecurityCode,
                mfaContact: [],
                loggingCustOff: false,
                loading: 'idle',
                gmMemberErr: '',
                gmAcctErr: '',
                gmRewardsLoading: false,
                vinCustReturned: false,
            };

            dispatch(changeCustInfo(newState));
        }
    };
}

export function SetChangeCustReturned(value: boolean) {
    return (dispatch) => {
        dispatch(setChangeCustReturned(value));
    };
}

export function ClearCustInfo() {
    return (dispatch) => {
        dispatch(clearCustInfo(initialState));
    };
}

export function CheckUniqueEmail(email: string, nameId: string, profileNum: string, store: string, branch: string,
    firstName: string, lastName: string, phone: string) {
    return async (dispatch, getState) => {
        const logId = getState().Framework.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            email: email,
            nameId: nameId,
            profile: profileNum,
            firstName: firstName,
            lastName: lastName,
            phone: phone,
            callingProgram: callingProgram,
            logId: logId,
        };
        const response = await acBase.eraWebServiceCall<Customer>('SVPController', 'CheckUniqueEmail', reqData, store, branch);
        const { errMsg, retCode } = response;
        if (retCode && retCode === '8000') {
            dispatch(customerError(retCode));
        } else {
            dispatch(checkUniqueEmail(errMsg));
        }
    };
}

export function GetExCust(selectedNum: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const logId = getState().Framework.logId;
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const identityVerified = getState().Customer.identityVerified;
        const profileNum = getState().Framework.specProfile;
        const customer = getState().Customer.Customer;
        const emailAddress = getState().Appointment.ApptCust.custEmailAddress;
        const phoneNumber = getState().Appointment.ApptCust.custPhoneNumber;
        const gmRedeemAmt = getState().Customer.Customer.gmRedeemAmt;
        // const emailAddress = getState().Appointment.SearchFields?.searchEmail;
        // const phoneNumber = getState().Appointment.SearchFields?.searchPhone;
        // let hideData = true;
        const login = getState().Customer.Customer.login;
        // if (login) {
        //     hideData = false;
        // }
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }

        const reqData = {
            email: emailAddress?.includes('*') ? '' : emailAddress,
            phone: phoneNumber,
            firstName: '',
            lastName: '',
            profile: profileNum,
            logId: logId,
            nameId: selectedNum,
            allowMulti: 'Y',
            callingProgram: callingProgram,
            // hideData: hideData,
            // selectedNum: selectedNum,
        };

        const response = await acBase.eraWebServiceCall('SVPController', 'CustSearch', reqData, store, branch);

        if (response.retCode && response.retCode === '8000') {
            dispatch(customerError(response.retCode));
        } else {
            const email = emailAddress === '' || identityVerified ? response.emailAddress : emailAddress;
            const phone = phoneNumber === '' || identityVerified ? response.phoneNumber : phoneNumber;

            const Newcustomer: Customer = {
                nameId: customer.nameId,
                firstName: response.firstName,
                lastName: response.lastName,
                emailAddress: email,
                phoneNumber: phone,
                contactOrder: response.contactOrder,
                login: login,
                prefContact: response.prefContact,
                textOptIn: response.textOptIn,
                emailOptIn: response.emailOptIn,
                streetAddress: response.streetAddress ? response.streetAddress : initialState.Customer.streetAddress,
                city: response.city ? response.city : initialState.Customer.city,
                stateAddress: response.stateAddress ? response.stateAddress : initialState.Customer.stateAddress,
                zipCode: response.zipCode ? response.zipCode : initialState.Customer.zipCode,
                errMsg: response.errMsg,
                // vehicleList: identityVerified ? response.vehicleList : customer.vehicleList,
                vehicleList: customer.vehicleList,
                phoneType: response.phoneType,
                retCode: response.retCode,
                addVehNameId: customer.addVehNameId,
                hasAccount: response.hasAccount,
                employeeFlag: response.employeeFlag,
                gmRedeemAmt: gmRedeemAmt,
                encryptFirst: response.encryptFirst,
                encryptLast: response.encryptLast,
                encryptEmail: response.encryptEmail,
                encryptPhone: response.encryptPhone,
                ascCustId: response.ascCustId ? response.ascCustId : initialState.Customer.ascCustId,
            };
            dispatch(getExCust(Newcustomer));
            dispatch(setMFAContact([response.textPhone1, response.textPhone2, response.mfaEmail]));
        }
    };
}

export function SendSecurityCode(contact: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const contactOrder = getState().Customer.Customer.contactOrder;
        let selectedContact = '';
        const selectedVehicle = getState().Vehicles.selectedVehicle;
        let nameId : string | undefined = getState().Customer.Customer.nameId;
        if (!nameId && selectedVehicle) {
            nameId = selectedVehicle;
        }
        const logId = getState().Framework.logId;
        const profileNum = getState().Framework.specProfile;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        if (contactOrder) {
            // If a contact method of -1 was passed, we select the last value in the contactOrder list,
            // which should always be 4 (email).
            if (parseInt(contact) === -1) {
                selectedContact = contactOrder[contactOrder.length - 1];
            } else {
                selectedContact = contactOrder[parseInt(contact)];
            }
        }
        const reqData = {
            contact: selectedContact,
            nameId: nameId,
            logId: logId,
            callingProgram: callingProgram,
            profileNum: profileNum,
            // selectedNum: '0',
        };

        const response = await acBase.eraWebServiceCall('SVPController', 'SendSecurityCode', reqData, store, branch);
        const { sentSecurityCode, retCode } = response;
        if (retCode && retCode === '8000') {
            dispatch(customerError(retCode));
        } else {
            if (sentSecurityCode === 'true') {
                dispatch(sendSecurityCode(true));
            } else {
                dispatch(sendSecurityCode(false));
            }
        }
    };
}

export function VerifyIdentity(securityCode: string, rememberMe: boolean) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const logId = getState().Framework.logId;
        const selectedVehicle = getState().Vehicles.selectedVehicle;
        let nameId : string | undefined = getState().Customer.Customer.nameId;
        if (!nameId && selectedVehicle) {
            nameId = selectedVehicle;
        }
        // const currEmail = getState().Customer.Customer.emailAddress;
        // const currLastName = getState().Customer.Customer.lastName;
        // const currPhone = getState().Customer.Customer.phoneNumber;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }

        const reqData = {
            nameId: nameId,
            verificationCode: securityCode,
            logId: logId,
            callingProgram: callingProgram,
            rememberMe: rememberMe,
            // selectedNum: '0',
        };
        const response = await acBase.eraWebServiceCall('SVPController', 'VerifyIdentity', reqData, store, branch);
        // const { identityVerified, errMsg, lastName, emailAddress, phoneNumber, authToken } = response;
        const { identityVerified, errMsg, authToken, retCode } = response;

        if (retCode && retCode === '8000') {
            dispatch(customerError(retCode));
        } else {
            let returnCust : any = {
                identityVerified: false,
                // lastName: currLastName,
                // emailAddress: currEmail,
                // phoneNumber: currPhone,
                errMsg: errMsg,
                login: false,
            };
            if (identityVerified === 'true') {
                returnCust = {
                    identityVerified: true,
                    firstName: response.firstName,
                    lastName: response.lastName,
                    emailAddress: response.emailAddress,
                    phoneNumber: response.phoneNumber,
                    errMsg: errMsg,
                    login: true,
                };
                // SetCookie('authToken', authToken);
                if (rememberMe && authToken && callingProgram === 'SVP') {
                    document.cookie = 'authToken=' + authToken + '; max-age=864000; Secure=true;';
                }

                if (rememberMe && authToken && callingProgram === 'OSD') {
                    document.cookie = `authTokenOSD=${authToken}; max-age=864000; Secure=true;`;
                }
            }

            dispatch(verifyIdentity(returnCust));
            // If the user exceeds the number of tries they need to
            // have a new code sent.
            if (returnCust.errMsg === '1003' || returnCust.identityVerified === true) {
                dispatch(sendSecurityCode(false));
            }
        }
    };
}

export function SetVerifyingIdentity(verifyingIdent: boolean) {
    return (dispatch) => {
        dispatch(setVerifyingIdentity(verifyingIdent));
    };
}

export function CheckAuthToken() {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        // const nameId = getState().Customer.Customer.nameId;
        const authToken = GetCookie('authToken');
        const logId = getState().Framework.logId;
        const selectedVehicle = getState().Vehicles.selectedVehicle;
        let nameId : string | undefined = getState().Customer.Customer.nameId;
        if (!nameId && selectedVehicle) {
            nameId = selectedVehicle;
        }
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            authToken: authToken,
            nameId: nameId,
            callingProgram: callingProgram,
            logId: logId,
            // selectedNum: '0',
        };

        // Identity verification is always required; always initiate token check.
        dispatch(setVerifyingIdentity(true));
        const response = await acBase.eraWebServiceCall('SVPController', 'CheckAuthToken', reqData, store, branch);
        if (response.retCode && response.retCode === '8000') {
            dispatch(customerError(response.retCode));
        } else {
            dispatch(checkAuthToken(response.errMsg));
            dispatch(setVerifyingIdentity(false));
        }
    };
}

export function CheckAuthOSDToken(customerNameId: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const nameId = customerNameId;
        const authToken = GetCookie('authTokenOSD');
        const logId = getState().Framework.logId;
        const callingProgram = 'OSD';

        const reqData = {
            authToken: authToken,
            nameId: nameId,
            callingProgram: callingProgram,
            logId: logId,
        };

        dispatch(setVerifyingIdentity(true));
        const response = await acBase.eraWebServiceCall('SVPController', 'CheckAuthToken', reqData, store, branch);
        if (response.retCode && response.retCode === '8000') {
            dispatch(customerError(response.retCode));
        } else {
            dispatch(checkAuthToken(response.errMsg));
            dispatch(setVerifyingIdentity(false));
        }
    };
}

export function SetSendSecurityCode(value: boolean) {
    return (dispatch) => {
        dispatch(sendSecurityCode(value));
    };
}

export function WaitForLogOff() {
    return (dispatch) => {
        dispatch(waitForLogOff());
    };
}

export function LogCustOff() {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const logId = getState().Framework.logId;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            callingProgram: callingProgram,
            logId: logId,
        };
        const response = await acBase.eraWebServiceCall('SVPController', 'LogCustOff', reqData, store, branch);
        // set logging off flag done
        const { retCode } = response;
        if (retCode && retCode === '8000') {
            dispatch(customerError(retCode));
        } else {
            dispatch(logCustOff());
            dispatch(clearGMInfo());
        }
    };
}

export function GMMemberSearch(email: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const logId = getState().Framework.logId;
        const nameId = getState().Customer.Customer.nameId;
        const callingProgram = 'SVP';
        const reqData = {
            callingProgram: callingProgram,
            logId: logId,
            email: email,
            nameId: nameId,
        };
        dispatch(setGMRewardsLoading(true));
        const response = await acBase.eraWebServiceCall('SVPController', 'GMMemberSearch', reqData, store, branch);
        const {gmDealerCanEarn, gmDealerCanRedeem, gmRedeemAmt, gmMemberErr} = response;
        const gmMemberResponse = {
            gmRedeemAmt: gmRedeemAmt,
            gmMemberErr: gmMemberErr,
        };
        const gmDealerEligibilityResponse = {
            gmDealerCanEarn: gmDealerCanEarn,
            gmDealerCanRedeem: gmDealerCanRedeem,
        };
        dispatch(gmMemberSearch(gmMemberResponse));
        dispatch(gmDealerEligibility(gmDealerEligibilityResponse));
        dispatch(setGMRewardsLoading(false));
    };
}

export function ClearGMInfo() {
    return (dispatch) => {
        dispatch(clearGMInfo());
    };
}

export function VINCustSearch(custVin: string) {
    return async (dispatch, getState: () => IStoreState) => {
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const logId = getState().Framework.logId;
        const callingProgram = 'SVP';
        const reqData = {
            callingProgram: callingProgram,
            logId: logId,
            custVin: custVin,
        };
        const response = await acBase.eraWebServiceCall<Customer>('SVPController', 'VINCustSearch', reqData, store, branch);
        if (response.retCode && (response.retCode === '1004' || response.retCode === '1005')) {
            dispatch(customerError(response.errMsg));
        } else {
            const vinSearchCustomer: Customer = {
                nameId: response.ascCustId,
                firstName: response.firstName,
                lastName: response.lastName,
                emailAddress: response.emailAddress,
                phoneNumber: response.phoneNumber,
                contactOrder: response.contactOrder,
                login: false,
                prefContact: response.prefContact,
                textOptIn: response.textOptIn,
                emailOptIn: response.emailOptIn,
                streetAddress: response.streetAddress ? response.streetAddress : initialState.Customer.streetAddress,
                city: response.city ? response.city : initialState.Customer.city,
                stateAddress: response.stateAddress ? response.stateAddress : initialState.Customer.stateAddress,
                zipCode: response.zipCode ? response.zipCode : initialState.Customer.zipCode,
                errMsg: response.errMsg,
                vehicleList: response.vehicleList,
                phoneType: response.phoneType,
                retCode: response.retCode,
                addVehNameId: response.addVehNameId,
                hasAccount: response.hasAccount,
                employeeFlag: response.employeeFlag,
                gmRedeemAmt: '',
                encryptFirst: response.encryptFirst,
                encryptLast: response.encryptLast,
                encryptEmail: response.encryptEmail,
                encryptPhone: response.encryptPhone,
                ascCustId: response.ascCustId ? response.ascCustId : initialState.Customer.ascCustId,
            };
            dispatch(vinCustSearch(vinSearchCustomer));
            dispatch(setVinCustReturned(true));
        }
    };
}

export function SetVinCustReturned(value: boolean) {
    return (dispatch) => {
        dispatch(setVinCustReturned(value));
    };
}